/**
 * Analytics Service
 * Track user events with Google Analytics 4
 */

// Extend window type for gtag
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

// Event categories
export const EVENT_CATEGORIES = {
  CONVERSION: 'conversion',
  TOOL_USAGE: 'tool_usage',
  FILE_OPERATION: 'file_operation',
  NAVIGATION: 'navigation',
  ENGAGEMENT: 'engagement',
} as const;

// Event actions
export const EVENT_ACTIONS = {
  // File operations
  FILE_UPLOAD: 'file_upload',
  FILE_DOWNLOAD: 'file_download',
  CODE_COPY: 'code_copy',
  
  // Tool usage
  SVG_TO_CODE: 'svg_to_code_convert',
  PNG_TO_SVG: 'png_to_svg_convert',
  FAVICON_GENERATE: 'favicon_generate',
  IMAGE_COMPRESS: 'image_compress',
  IMAGE_CONVERT: 'image_convert',
  SVG_EDIT: 'svg_edit',
  
  // Engagement
  TOOL_SELECT: 'tool_select',
  EXTERNAL_LINK: 'external_link_click',
  SUPPORT_CLICK: 'support_button_click',
} as const;

/**
 * Check if Google Analytics is loaded
 */
function isGtagAvailable(): boolean {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
}

/**
 * Track a custom event
 */
export function trackEvent(
  action: string,
  category: string,
  label?: string,
  value?: number,
  additionalParams?: Record<string, unknown>
): void {
  if (!isGtagAvailable()) {
    console.debug('[Analytics] gtag not available, skipping event:', action);
    return;
  }

  try {
    window.gtag!('event', action, {
      event_category: category,
      event_label: label,
      value: value,
      ...additionalParams,
    });
    console.debug('[Analytics] Event tracked:', { action, category, label, value });
  } catch (error) {
    console.error('[Analytics] Failed to track event:', error);
  }
}

/**
 * Track page view (for SPA navigation)
 */
export function trackPageView(path: string, title: string): void {
  if (!isGtagAvailable()) return;

  try {
    window.gtag!('event', 'page_view', {
      page_path: path,
      page_title: title,
      page_location: window.location.origin + path,
    });
    console.debug('[Analytics] Page view tracked:', path);
  } catch (error) {
    console.error('[Analytics] Failed to track page view:', error);
  }
}

/**
 * Track file upload event
 */
export function trackFileUpload(
  toolName: string,
  fileType: string,
  fileSize: number
): void {
  trackEvent(EVENT_ACTIONS.FILE_UPLOAD, EVENT_CATEGORIES.FILE_OPERATION, toolName, fileSize, {
    file_type: fileType,
    file_size_kb: Math.round(fileSize / 1024),
  });
}

/**
 * Track file download event
 */
export function trackFileDownload(
  toolName: string,
  fileType: string,
  fileSize?: number
): void {
  trackEvent(EVENT_ACTIONS.FILE_DOWNLOAD, EVENT_CATEGORIES.FILE_OPERATION, toolName, fileSize, {
    file_type: fileType,
  });
}

/**
 * Track code copy event
 */
export function trackCodeCopy(toolName: string, codeLength?: number): void {
  trackEvent(EVENT_ACTIONS.CODE_COPY, EVENT_CATEGORIES.FILE_OPERATION, toolName, codeLength);
}

/**
 * Track conversion event
 */
export function trackConversion(
  action: string,
  toolName: string,
  details?: Record<string, unknown>
): void {
  trackEvent(action, EVENT_CATEGORIES.CONVERSION, toolName, undefined, details);
}

/**
 * Track tool selection
 */
export function trackToolSelect(toolName: string): void {
  trackEvent(EVENT_ACTIONS.TOOL_SELECT, EVENT_CATEGORIES.NAVIGATION, toolName);
}

/**
 * Track external link click
 */
export function trackExternalLink(url: string, linkName: string): void {
  trackEvent(EVENT_ACTIONS.EXTERNAL_LINK, EVENT_CATEGORIES.ENGAGEMENT, linkName, undefined, {
    link_url: url,
  });
}

/**
 * Track support button click
 */
export function trackSupportClick(): void {
  trackEvent(EVENT_ACTIONS.SUPPORT_CLICK, EVENT_CATEGORIES.ENGAGEMENT, 'buy_me_coffee');
}

// Convenience functions for each tool
export const analytics = {
  svgToCode: {
    convert: (fileSize: number) => trackConversion(EVENT_ACTIONS.SVG_TO_CODE, 'SVG to Code', { file_size: fileSize }),
    copy: (codeLength: number) => trackCodeCopy('SVG to Code', codeLength),
    download: (fileSize: number) => trackFileDownload('SVG to Code', 'svg', fileSize),
  },
  pngToSvg: {
    convert: (inputFormat: string) => trackConversion(EVENT_ACTIONS.PNG_TO_SVG, 'PNG to SVG', { input_format: inputFormat }),
    download: () => trackFileDownload('PNG to SVG', 'svg'),
  },
  favicon: {
    generate: (inputFormat: string) => trackConversion(EVENT_ACTIONS.FAVICON_GENERATE, 'Favicon Generator', { input_format: inputFormat }),
    downloadPackage: () => trackFileDownload('Favicon Generator', 'zip'),
    downloadSingle: (size: number) => trackFileDownload('Favicon Generator', 'png', size),
  },
  compress: {
    compress: (originalSize: number, compressedSize: number, format: string) => 
      trackConversion(EVENT_ACTIONS.IMAGE_COMPRESS, 'Image Compressor', {
        original_size: originalSize,
        compressed_size: compressedSize,
        compression_ratio: Math.round((1 - compressedSize / originalSize) * 100),
        format,
      }),
    download: (fileSize: number, format: string) => trackFileDownload('Image Compressor', format, fileSize),
  },
  convert: {
    convert: (inputFormat: string, outputFormat: string) => 
      trackConversion(EVENT_ACTIONS.IMAGE_CONVERT, 'Image Converter', {
        input_format: inputFormat,
        output_format: outputFormat,
      }),
    download: (format: string) => trackFileDownload('Image Converter', format),
  },
  svgEditor: {
    edit: (editType: string) => trackConversion(EVENT_ACTIONS.SVG_EDIT, 'SVG Editor', { edit_type: editType }),
    download: () => trackFileDownload('SVG Editor', 'svg'),
  },
  pageView: trackPageView,
  toolSelect: trackToolSelect,
  externalLink: trackExternalLink,
  supportClick: trackSupportClick,
};
